
export type Html <H extends HTMLElement | SVGElement> = {
  replaceChildren(...els: (Html <any> | string)[]): Html<any>;
  append(...els: (Html <any> | string)[]): Html<any>;
  onclick(handler: (event: MouseEvent) => void): Html<any>;
  style(styles: Partial<CSSStyleDeclaration>): Html<any>;
  el: H;
  content: (s:string)=>Html<any>;
  setAttribute(name: string, value: string): Html<any>;
}


export function fromElement <H extends HTMLElement | SVGElement>(el: H): Html<H> {
  return {
    replaceChildren(...els: (Html<any> | string)[]): Html<any> {
      el.replaceChildren(...els.map(x=> typeof x == "string" ? x : x.el))
      return this;
    },
    append(...els: (Html<any> | string)[]): Html<any> {
      el.append(...els.map(x=> typeof x == "string" ? x : x.el))
      return this;
    },
    content(s: string):Html<any>{
      el.textContent = s
      return this
    },
    onclick(handler: (event: MouseEvent) => void): Html<any> {
      el.addEventListener("click", handler as any);
      return this;
    },
    style(styles: Partial<CSSStyleDeclaration>): Html<any> {
      Object.assign(el.style, styles);
      return this;
    },
    setAttribute(name: string, value: string): Html<any> {
      el.setAttribute(name, value);
      return this;
    },
    el,
  }
}

let mkTag =  <T extends keyof HTMLElementTagNameMap> (tag: T) => (...x: (Html<any> | string)[]) => fromElement(document.createElement(tag)).append(...x) as Html<HTMLElementTagNameMap[T]>;

export const details = mkTag("details");
export const summary = mkTag("summary");
export const p = mkTag("p");
export const pre = mkTag("pre");
export const div = mkTag("div");
export const span = mkTag("span");
export const h1 = mkTag("h1");
export const h2 = mkTag("h2");
export const h3 = mkTag("h3");
export const datalist = mkTag("datalist");
export const select = mkTag("select");
export const label = mkTag("label");
export const option = mkTag("option");
export const ul = mkTag("ul");
export const li = mkTag("li");
export const a = mkTag("a");

const safeTags = new Set([
  "a", "article", "aside", "b", "blockquote", "br", "caption", "code", "dd", "del", "details", "div", "dl", "dt", "em",
  "figcaption", "figure", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "img", "li", "main", "mark", "ol", "p",
  "pre", "section", "small", "span", "strong", "sub", "summary", "sup", "table", "tbody", "td", "tfoot", "th", "thead", "tr", "u", "ul",
])
const safeAttributes = new Set(["alt", "class", "colspan", "dir", "height", "id", "lang", "rel", "role", "rowspan", "src", "style", "target", "title", "width"])
const safeUrl = (value: string) => /^(?:https?:|mailto:|\/|\.\/|\.\.\/|#)/i.test(value.trim())

export function safeHtml(source: string): Html<HTMLDivElement> {
  const parsed = new DOMParser().parseFromString(source, "text/html")
  const root = document.createElement("div")

  const copy = (node: Node, parent: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) { parent.appendChild(document.createTextNode(node.textContent ?? "")); return }
    if (!(node instanceof Element)) return
    const tag = node.tagName.toLowerCase()
    if (!safeTags.has(tag)) { for (const child of node.childNodes) copy(child, parent); return }

    const element = document.createElement(tag)
    for (const attribute of node.attributes) {
      const name = attribute.name.toLowerCase()
      if (!safeAttributes.has(name) && !name.startsWith("aria-") && !name.startsWith("data-")) continue
      if ((name === "href" || name === "src") && !safeUrl(attribute.value)) continue
      if (name === "style" && /(?:url\s*\(|expression\s*\(|@import|-moz-binding)/i.test(attribute.value)) continue
      element.setAttribute(name, attribute.value)
    }
    if (tag === "a") {
      const href = node.getAttribute("href")
      if (href && safeUrl(href)) element.setAttribute("href", href)
      if (element.getAttribute("target") === "_blank") element.setAttribute("rel", "noopener noreferrer")
    }
    for (const child of node.childNodes) copy(child, element)
    parent.appendChild(element)
  }

  for (const child of parsed.body.childNodes) copy(child, root)
  return fromElement(root)
}

export const link = (title: string, href: string) => a(title).setAttribute('href', href).style({
  color: palette.accent, textDecoration:"none"
})

export const button = (title: string, onclick: (e: MouseEvent) => void | Promise<void>) => {
  const result = mkTag("button")(title).style({
    cursor: "pointer",
    color: palette.text,
    backgroundColor: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: "2px",
    padding: "7px 11px",
    margin: "0",
  })
  result.onclick(async event => {
    if (result.el.disabled) return
    result.el.disabled = true
    try { await onclick(event) }
    finally { result.el.disabled = false }
  })
  return result
}


export const input = mkTag("input");

export function svg(paths: string[], label?: string) {
  const root = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  root.setAttribute("viewBox", "0 0 24 24")
  root.setAttribute("width", "18")
  root.setAttribute("height", "18")
  root.setAttribute("fill", "none")
  root.setAttribute("stroke", "currentColor")
  root.setAttribute("stroke-width", "1.8")
  root.setAttribute("stroke-linecap", "round")
  root.setAttribute("stroke-linejoin", "round")
  root.setAttribute("aria-hidden", label ? "false" : "true")
  if (label) root.setAttribute("aria-label", label)
  for (const data of paths) {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("d", data)
    root.append(path)
  }
  return fromElement(root).style({flexShrink: "0"})
}

export const textarea = (value: string) => {
  let el = document.createElement("textarea")


  el.value = value
  let oninput = ()=>{
    el.rows = Math.min(20, el.value.split("\n").length + 1)
    el.cols = Math.min(100, el.value.split("\n").reduce((max, line) => Math.max(max, line.length), 0))
  }
  oninput()
  el.oninput = oninput
  let res = fromElement(el)
  .style({
    width: "100%",
    boxSizing: "border-box",
  })
  return res
}

export const palette = {
  background: "var(--color-background)",
  surface: "var(--color-surface)",
  text: "var(--color-text)",
  mutedText: "var(--color-muted-text)",
  border: "var(--color-border)",
  accent: "var(--color-accent)",
  accentText: "var(--color-accent-text)",
  success: "var(--color-success)",
  successText: "var(--color-success-text)",
  warning: "var(--color-warning)",
  warningText: "var(--color-warning-text)",
  danger: "var(--color-danger)",
  dangerText: "var(--color-danger-text)",
} as const

export let block : typeof div = (...x) => div(...x).style({
  display: "block",
  border: `1px solid ${palette.border}`,
  padding: "16px",
  borderRadius: "2px",
  margin: "12px 0",
  background: palette.surface,
})

export const body = fromElement(document.body).style({
  fontFamily: "Arial, Helvetica, ui-sans-serif, system-ui, sans-serif",
  margin: "0",
});

let isdev = location.hostname.includes("localhost")

const styles = document.createElement("style")
styles.textContent = `
  :root {
    --etindi-header-height: 128px;
    color-scheme: light dark;
    --color-background: #f4f5f7;
    --color-surface: #ffffff;
    --color-text: #252a31;
    --color-muted-text: #69707a;
    --color-border: #d4d7dc;
    --color-accent: ${isdev ? "#1a451a": "#2855b6"};
    --color-accent-text: #ffffff;
    --color-success: #d1e7dd;
    --color-success-text: #0f5132;
    --color-warning: #fff3cd;
    --color-warning-text: #664d03;
    --color-danger: #f8d7da;
    --color-danger-text: #842029;
  }

  body {
    background: var(--color-background);
    color: var(--color-text);
    -webkit-font-smoothing: antialiased;
  }

  h1, h2, h3 {
    color: var(--color-text);
    font-weight: 600;
  }

  h2 { font-size: 21px; }
  p { margin: 10px 0; }

  button, input, select, textarea {
    font: inherit;
  }

  button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible, summary:focus-visible {
    outline: 3px solid color-mix(in srgb, var(--color-accent) 28%, transparent);
    outline-offset: 2px;
  }

  button:hover:not(:disabled), a:hover { filter: none; }
  button:hover:not(:disabled) { background-color: color-mix(in srgb, var(--color-accent) 6%, var(--color-surface)); }
  button:disabled { cursor: wait; opacity: .6; }

  input, select, textarea {
    color: var(--color-text);
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: 2px;
  }

  pre {
    padding: 12px;
    overflow: auto;
    background: color-mix(in srgb, var(--color-surface) 78%, var(--color-background));
    border: 1px solid var(--color-border);
    border-radius: 0;
  }

  summary::marker {
    color: var(--color-muted-text);
  }

  body:has(.etindi-explorer-workspace) {
    color-scheme: light;
    --color-background: #f8f9fa;
    --color-surface: #ffffff;
    --color-text: #111111;
    --color-muted-text: #666b73;
    --color-border: #dedfe3;
    --color-accent: #34446e;
    --color-accent-text: #ffffff;
  }

  .etindi-app-header {
    box-sizing: border-box;
    min-height: var(--etindi-header-height);
    display: flex;
    align-items: center;
    padding: 24px 32px;
    background: ${ isdev ? "#346e41" : "#34446e"};
    justify-content: space-between;
    gap: 24px;
    position: sticky;
    top: 0;
    z-index: 200;
  }
  .etindi-app-header a { font-size: 40px; font-weight: 700; letter-spacing: -.02em; color: white !important; }
  .etindi-app-header > div { min-width: 0; }
  .etindi-header-picker { display: grid; gap: 4px; color: white; font-size: 16px; }
  .etindi-header-picker select {
    width: 240px; max-width: 100%; padding: 7px 10px;
    background: white; color: #111; border: 1px solid #d4d7dc;
    border-radius: 4px; font-weight: 600;
  }
  .etindi-module-navigation {
    position: sticky;
    top: var(--etindi-header-height);
    z-index: 150;
    background: var(--color-background);
  }
  .etindi-chat-navigation {
    position: sticky;
    top: calc(var(--etindi-header-height) + 39px);
    z-index: 140;
    margin: 0 0 24px !important;
    padding: 3px 0;
    background: var(--color-background);
    border-bottom: 1px solid var(--color-border);
  }
  .etindi-chat-navigation button {
    padding: 4px 8px !important;
  }
  body:has(.etindi-module-navigation) .etindi-status-area {
    top: calc(var(--etindi-header-height) + 55px) !important;
  }
  body:has(.etindi-chat-navigation) .etindi-status-area {
    top: calc(var(--etindi-header-height) + 96px) !important;
  }
  .etindi-module-navigation:has(+ div .etindi-chat-navigation) {
    margin-bottom: 0 !important;
  }
  .etindi-page > div:has(.etindi-chat-navigation) {
    margin-top: 0 !important;
  }
  .etindi-chat-composer {
    position: sticky;
    bottom: 0;
    z-index: 130;
    margin-bottom: 0 !important;
    background: var(--color-surface) !important;
    box-shadow: 0 -4px 12px color-mix(in srgb, var(--color-text) 8%, transparent);
    padding: 8px !important;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    gap: 8px;
  }
  .etindi-chat-composer textarea { min-height: 44px !important; height: 44px; resize: vertical; }
  .etindi-chat-composer > div { margin: 0 !important; }
  .etindi-chat-composer > p { grid-column: 1 / -1; margin: 0 !important; }
  .etindi-chat-composer > p:empty { display: none; }
  .etindi-page:has(.etindi-chat-composer),
  .etindi-page > div:has(.etindi-chat-composer) {
    padding-bottom: 0 !important;
    margin-bottom: 0 !important;
  }
  .etindi-page:has(.etindi-explorer-workspace) { padding: 0 !important; }
  .etindi-explorer-workspace { line-height: 1.5; overflow-wrap: anywhere; }
  .etindi-split-view {
    display: grid;
    grid-template-columns: 384px minmax(0, 1fr);
    min-height: calc(100vh - 128px);
    background: var(--color-surface);
  }
  .etindi-explorer-sidebar {
    padding: 16px 24px;
    background: var(--color-background);
    border-right: 1px solid var(--color-border);
  }
  .etindi-explorer-sidebar > h2 { margin-left: -8px !important; }
  .etindi-explorer-content { min-width: 0; padding: 24px 24px 56px; }
  .etindi-eyebrow {
    margin: 0 0 8px;
    color: var(--color-text) !important;
    font-size: 12px !important;
    font-weight: 700;
    text-transform: uppercase;
  }
  .etindi-explorer-control {
    width: 100%;
    box-sizing: border-box;
    margin: 0 0 20px;
    padding: 9px 10px;
    border: 1px solid #181818;
    border-radius: 8px;
    min-height: 40px;
    font-size: 14px;
  }
  .etindi-explorer-control::placeholder { color: #8b9097; }
  .etindi-search { position: relative; }
  .etindi-search > svg { position: absolute; left: 12px; top: 12px; width: 16px; height: 16px; }
  .etindi-search > input { padding-left: 36px !important; }
  .etindi-explorer-content div:has(> .etindi-property-grid) {
    background: var(--color-background) !important; border: 0 !important;
    border-radius: 8px !important; box-shadow: 0 1px 8px #0000000c;
  }
  .etindi-explorer-content .etindi-property-grid > div:first-child { padding-top: 0 !important; }
  .etindi-explorer-content .etindi-property-grid > div:last-child { padding-bottom: 0 !important; }
  .etindi-explorer-content .etindi-property-grid pre { border-radius: 8px; background: transparent; margin-bottom: 0; }
  .etindi-tree-menu { padding: 0 8px; }
  .etindi-tree-menu > button {
    display: block; width: 100%; text-align: left; padding: 12px 8px !important;
    font-size: 18px; background: transparent !important;
    min-height: 52px;
    border: 0 !important;
    border-radius: 4px !important;
    margin: 4px 0 !important;
  }
  .etindi-tree-menu > button[aria-current="page"] {
    background: var(--color-accent) !important;
    color: white !important;
    font-weight: 700 !important;
  }
  .etindi-breadcrumbs, .etindi-breadcrumbs > span {
    display: flex; flex-wrap: wrap; align-items: center; gap: 12px;
  }
  .etindi-breadcrumbs { font-size: 14px; }
  .etindi-explorer-content .etindi-breadcrumbs { margin: 0 0 26px; min-height: 24px; }
  .etindi-explorer-content .etindi-breadcrumbs button { color: var(--color-text) !important; }
  .etindi-explorer-content .etindi-breadcrumbs > span:last-child button { font-weight: 700 !important; }
  .etindi-explorer-header {
    display: flex; align-items: center; justify-content: space-between;
    gap: 16px; min-height: 48px; margin-bottom: 26px;
  }
  .etindi-explorer-header > .etindi-breadcrumbs { margin: 0; }
  .etindi-explorer-actions { display: flex; flex-wrap: wrap; gap: 8px; }
  .etindi-explorer-actions button { border-radius: 6px !important; }
  .etindi-tree-actions { display: contents; }
  button.etindi-circle-action {
    display: inline-grid;
    place-items: center;
    width: 48px;
    height: 48px;
    padding: 0 !important;
    border: 0;
    border-radius: 50% !important;
    color: white !important;
    background: var(--color-accent) !important;
    box-shadow: 0 2px 5px #00000024;
  }
  button.etindi-circle-action span span { display: none; }
  button.etindi-circle-action svg { width: 21px; height: 21px; }
  .etindi-explorer-folders { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 8px 32px; }
  button.etindi-explorer-tab {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 8px 16px !important; min-height: 42px;
    background: #f8f9fa !important; color: var(--color-text);
    border-radius: 4px !important;
  }
  button.etindi-explorer-tab[aria-pressed="true"] {
    background: var(--color-accent) !important; color: white !important; font-weight: 700;
  }
  .etindi-tile-grid { display: grid; gap: 8px; }
  button.etindi-tile {
    display: flex; align-items: center; gap: 16px;
    width: 100%; min-height: 48px; padding: 12px 16px !important;
    border: 1px solid var(--color-border); border-radius: 6px !important;
    box-shadow: 0 1px 1px #00000012;
    font-size: 18px; text-align: left;
    background: white;
  }
  button.etindi-tile[data-depth]:not([data-depth="0"]) {
    position: relative;
    border-left: 3px solid color-mix(in srgb, var(--color-accent) 35%, var(--color-border));
  }
  button.etindi-tile[data-depth="1"] { padding-left: 48px !important; }
  button.etindi-tile[data-depth="2"] { padding-left: 80px !important; }
  button.etindi-tile[data-depth="3"] { padding-left: 112px !important; }
  button.etindi-tile[data-depth="4"] { padding-left: 144px !important; }
  button.etindi-tile[data-depth="5"] { padding-left: 176px !important; }
  .etindi-item-tree { display: grid; gap: 8px; }
  .etindi-item-row {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) auto auto;
    align-items: center;
    min-height: 50px;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: 6px;
    box-shadow: 0 1px 1px #00000012;
  }
  button.etindi-item-toggle {
    align-self: stretch;
    padding: 0 !important;
    border: 0;
    background: transparent;
    color: var(--color-accent);
    font-size: 24px;
  }
  button.etindi-item-toggle:disabled { cursor: default; opacity: 0; }
  button.etindi-item-link {
    align-self: stretch;
    padding: 10px 8px !important;
    border: 0;
    background: transparent;
    text-align: left;
    font-size: 18px;
  }
  .etindi-item-actions { display: flex; align-items: center; gap: 2px; padding: 0 5px; }
  button.etindi-item-action {
    display: inline-grid; place-items: center;
    width: 34px; height: 34px; padding: 0 !important;
    border: 0; border-radius: 50% !important;
    color: var(--color-muted-text); background: transparent;
  }
  button.etindi-item-action:hover { color: var(--color-accent); background: var(--color-background); }
  button.etindi-item-action span span { display: none; }
  button.etindi-item-action svg { width: 17px; height: 17px; }
  button.etindi-item-delete:hover { color: #b42318; }
  .etindi-item-row > span { margin-right: 14px; }
  .etindi-source-references {
    margin: 20px 0;
    padding: 18px;
    background: var(--color-background);
    border-radius: 8px;
    box-shadow: 0 1px 8px #0000000c;
  }
  .etindi-child-items { margin: 24px 0; }
  button.etindi-source-reference {
    display: block;
    width: 100%;
    margin: 8px 0 0;
    padding: 14px 16px;
    text-align: left;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: 6px;
  }
  .etindi-source-path {
    color: var(--color-accent);
    font-size: 13px;
    font-weight: 700;
  }
  .etindi-source-excerpt {
    margin: 10px 0 0;
    padding-left: 14px;
    color: var(--color-text);
    border-left: 3px solid color-mix(in srgb, var(--color-accent) 35%, var(--color-border));
    font-style: italic;
  }
  .etindi-explorer-content .etindi-page-heading > h2 {
    color: var(--color-accent); font-size: 30px; font-weight: 700;
    padding-bottom: 12px; border-bottom: 1px solid var(--color-border);
  }
  .etindi-explorer-content .etindi-page-heading > h2 svg { display: none; }
  .etindi-explorer-content .etindi-page-heading > p {
    color: var(--color-text) !important; font-size: 16px !important;
    margin-top: 24px !important; padding: 16px;
    background: var(--color-background); border-radius: 8px;
    box-shadow: 0 1px 8px #0000000c;
  }
  .etindi-explorer-content .etindi-comments {
    margin-top: 32px !important; padding: 24px 0 !important;
    border: 0 !important; border-top: 1px solid var(--color-border) !important;
  }
  .etindi-comments > h2 { display: flex; align-items: center; gap: 16px; }
  .etindi-comments > h2::before, .etindi-comments > h2::after {
    content: ""; flex: 1; border-top: 1px solid var(--color-muted-text);
  }
  @media (max-width: 900px) {
    .etindi-split-view { grid-template-columns: 280px minmax(0, 1fr); }
    .etindi-explorer-header { align-items: flex-start; flex-direction: column; }
  }
  @media (max-width: 680px) {
    :root { --etindi-header-height: 80px; }
    .etindi-split-view { grid-template-columns: 1fr; }
    .etindi-explorer-sidebar { border-right: none; border-bottom: 1px solid var(--color-border); }
    .etindi-explorer-content { padding: 24px 20px 40px; }
  }

  @media (prefers-color-scheme: dark) {
    :root {
      --color-background: #0f1218;
      --color-surface: #1c212a;
      --color-text: #edf0f3;
      --color-muted-text: #a8b0bb;
      --color-border: #3b424c;
      --color-accent: ${ isdev ? "#669966" : "#7aa2ff"};
      --color-accent-text: #101318;
      --color-success: #173c2c;
      --color-success-text: #9ee6bd;
      --color-warning: #493900;
      --color-warning-text: #ffe08a;
      --color-danger: #7a0c19;
      --color-danger-text: #fa7885;
    }
  }
`
document.head.append(styles)
